import { FC, useState } from 'react';
import { DividerGroup, Row, RowActionEnum, SectionCard } from './DocumentLayout';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import { preFillZero } from 'src/app/common/utils';
import { BaseDialog } from './BaseDialog';
import {
  AppStatusEnum,
  UploadSignedDocAgentInfo,
  UploadSignedDocFileInfo,
  UploadSignedDocData,
  PdfFileTypeEnum,
  ChangeRequestItem,
  ChangeRequestSectionEnum,
} from 'src/app/modules/Recruit/types/personal-particular-types';
import {
  addUplineAgentInfo,
  deleteCustomDocument,
  deleteUplineAgentInfo,
  getUplineAgentInfo,
  uploadCustomDocument,
} from 'src/app/modules/Recruit/network/personalParticularCrud';
import { useLang } from 'src/app/i18n';
import { useDocument } from './useDocument';
import { apiErrorHandler } from 'src/app/common/network';

const MANAGER_TABLE_COLUMNS = [
  { key: 'uplineManager', labelKey: 'recruit.uploadSignedDocs.uplineManager' },
  { key: 'agentCode', labelKey: 'recruit.uploadSignedDocs.agentCode' },
  { key: 'agentName', labelKey: 'recruit.uploadSignedDocs.agentName' },
  { key: 'title', labelKey: 'recruit.uploadSignedDocs.destination' },
  { key: 'status', labelKey: 'recruit.uploadSignedDocs.agentStatus' },
  { key: 'email', labelKey: 'recruit.uploadSignedDocs.emailAddress' },
];

const useStyles = makeStyles()(() => ({
  itemContent: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 32,
    fontSize: '1rem',
  },
  submittedDate: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 32,
    fontSize: '1rem',
    color: '#848484',
  },
  managerCell: {
    minHeight: 32,
    padding: '6px 8px',
    lineHeight: '20px',
    boxSizing: 'border-box',
    overflowWrap: 'anywhere',
  },
  managerHeaderCell: {
    minHeight: 32,
    padding: '6px 8px',
    lineHeight: '20px',
    fontWeight: 600,
    boxSizing: 'border-box',
    overflowWrap: 'anywhere',
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    gap: 10,
  },
  managerInfoCells: {
    display: 'contents',
  },
  managerTable: {
    display: 'grid',
    gridTemplateColumns: `repeat(${MANAGER_TABLE_COLUMNS.length}, auto) 40px`,
    alignItems: 'stretch',
    rowGap: 10,
    width: '100%',
    padding: '0 24px',
  },
  managerRow: {
    display: 'contents',
  },
  managerActionCell: {
    width: 40,
    height: 40,
    padding: 8,
  },
  agentInfo: {
    display: 'contents',
  },
  agentCodeInputContainer: {
    gridColumn: '2 / 7',
    display: 'flex',
    alignItems: 'start',
    gap: 16,
    minWidth: 0,
  },
  agentCodeInput: {
    width: 280,
    height: 32,
    borderRadius: 4,
    padding: '0 12px',
    color: '#364153',
    outline: 'none',
    boxSizing: 'border-box',
  },
  agentCodeError: {
    marginTop: 2,
    fontSize: 12,
    color: '#E1001F',
  },
  confirmButtonContainer: {
    paddingTop: 0,
  },
  reminderMessage: {
    fontSize: '1rem',
    lineHeight: '22px',
    color: '#E1001F',
  },
  confirmAddSpan: {
    color: '#364153',
    fontWeight: 600,
  },
  managerDetailList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  managerDetailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 28,
    fontSize: '1rem',
    color: '#364153',
  },
  fileInfoSpan: {
    color: '#848484',
    marginRight: 10,
  },
  progressContainer: {
    width: 300,
  },
  reminder: {
    marginTop: 16,
    fontSize: 14,
    lineHeight: '20px',
    color: 'red',
  },
}));

type AppData = {
  appId: string;
  appStatus: string;
};

type UploadSignedDocProps = {
  appData?: AppData;
  uploadSignedDocData?: UploadSignedDocData;
  changeRequestData?: ChangeRequestItem[];
  onReloadData?: () => void;
};
export const UploadSignedDocSection: FC<UploadSignedDocProps> = ({
  appData,
  uploadSignedDocData,
  changeRequestData,
  onReloadData,
}) => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const { classes } = useStyles();

  if (
    !appData ||
    !appData.appId ||
    ![AppStatusEnum.SUBMITTED_TO_A0, AppStatusEnum.FOLLOW_UP_REQUIRED, AppStatusEnum.COMPLETED].includes(
      appData.appStatus as AppStatusEnum,
    )
  ) {
    return null;
  }

  const { financingAgreement, managerLiabilities, agentList } = uploadSignedDocData || {};

  const disabledUpload = changeRequestData?.some((i) => i.section === ChangeRequestSectionEnum.FINANCING_PACKAGE);

  return (
    <>
      <SectionCard title={t('recruit.uploadSignedDocs.title')}>
        <DividerGroup>
          {/* Financial Agreement */}
          <FileUploadRow
            appData={appData}
            disabledUpload={disabledUpload}
            fileInfo={{ ...financingAgreement, fileType: PdfFileTypeEnum.FinancingAgreement }}
            onReloadData={onReloadData}
          />
          {/* Manager Liabilities */}
          <FileUploadRow
            appData={appData}
            disabledUpload={disabledUpload}
            fileInfo={{ ...managerLiabilities, fileType: PdfFileTypeEnum.ManagerLiabilities }}
            onReloadData={onReloadData}
          />
          {/* List of upline manager has already signed the manager liabilities */}
          <ManagerLiabilityManagers
            appData={appData}
            disabledAdd={!managerLiabilities}
            dataList={agentList}
            onReloadData={onReloadData}
          />
        </DividerGroup>
      </SectionCard>

      {/* Reminder */}
      <div className={classes.reminder}>
        <div>{t('recruit.uploadSignedDocs.reminder')}:</div>
        <div>{t('recruit.uploadSignedDocs.reminder1')}</div>
        <div>{t('recruit.uploadSignedDocs.reminder2')}</div>
      </div>
    </>
  );
};

const ROW_TRANSLATION = {
  [PdfFileTypeEnum.FinancingAgreement]: {
    nameKey: 'recruit.uploadSignedDocs.financialAgreement',
    uploadSuccessKey: 'recruit.uploadSignedDocs.financingAgreementUploaded',
    deleteKey: 'recruit.uploadSignedDocs.deleteFinancialAgreementMessage',
  },
  [PdfFileTypeEnum.ManagerLiabilities]: {
    nameKey: 'recruit.uploadSignedDocs.managerLiabilities',
    uploadSuccessKey: 'recruit.uploadSignedDocs.managerLiabilitiesUploaded',
    deleteKey: 'recruit.uploadSignedDocs.deleteManagerLiabilitiesMessage',
  },
};

type FileUploadRowProps = {
  appData: AppData;
  fileInfo: UploadSignedDocFileInfo;
  disabledUpload?: boolean;
  onReloadData?: () => void;
};

export const FileUploadRow = ({ appData, fileInfo, disabledUpload, onReloadData }: FileUploadRowProps) => {
  const { appId, appStatus } = appData;
  const { fileType, fileName = '', fileSize = 0 } = fileInfo;

  const { classes } = useStyles();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  const dispatch = useDispatch();
  const { openFile } = useDocument();

  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [dialogOpen, setDialogOpen] = useState(false);

  const canUpload = [AppStatusEnum.SUBMITTED_TO_A0, AppStatusEnum.FOLLOW_UP_REQUIRED].includes(
    appStatus as AppStatusEnum,
  );

  const isCompleted = appStatus === AppStatusEnum.COMPLETED;

  const translation = ROW_TRANSLATION[fileType];

  const withLoading = async (key: string, callback: () => Promise<void>) => {
    setLoadingMap((prev) => ({
      ...prev,
      [key]: true,
    }));

    try {
      await callback();
    } finally {
      setLoadingMap((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };
  const handlePdfUpload = async (e?: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!e?.target.files?.length) {
        return;
      }

      const uploadFile = e.target.files[0];

      if (!/^(?=.*[A-Za-z0-9])[A-Za-z0-9 -]+\.pdf$/i.test(uploadFile.name)) {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: '',
              content: t('recruit.uploadSignedDocs.fileNameInvalid'),
            },
          ]),
        );
        return;
      }

      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      await uploadCustomDocument(appId, {
        fileName: uploadFile.name,
        fileType: fileType,
        fileData: base64,
      });

      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: t(translation.uploadSuccessKey),
          },
        ]),
      );
      await onReloadData?.();
    } catch (err) {
      console.log(err);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: '',
            content: t('recruit.uploadSignedDocs.systemBusy'),
          },
        ]),
      );
    }
  };

  const handleConfirm = async () => {
    try {
      await deleteCustomDocument(
        appId,
        {
          fileName: fileName,
          fileType: fileType,
        },
        dispatch,
      );
      await onReloadData?.();
      setDialogOpen(false);
    } catch (err) {
      console.log(err);
    }
  };
  const handleClose = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <Row
        actionAlign="top"
        actions={[
          {
            type: RowActionEnum.VIEW,
            onClick: () => withLoading(RowActionEnum.VIEW, () => openFile(appId, fileType, fileName)),
            visible: !!fileName,
            loading: !!loadingMap[RowActionEnum.VIEW],
          },
          {
            type: RowActionEnum.UPLOAD_PDF,
            onClick: (e) =>
              withLoading(RowActionEnum.UPLOAD_PDF, () => {
                return handlePdfUpload(e);
              }),
            disabled: !!fileName || !canUpload || disabledUpload,
            loading: !!loadingMap[RowActionEnum.UPLOAD_PDF],
          },
        ]}
      >
        <div className={classes.itemContent}>{t(translation.nameKey)}</div>

        <div className={classes.submittedDate}>
          {fileName && (
            <div>
              <span className={classes.fileInfoSpan}>
                {`${t('recruit.uploadSignedDocs.fileName')}: ${fileName},
                ${t('recruit.uploadSignedDocs.fileSize')}: ${Math.round(fileSize / 1024)}KB`}
              </span>

              {!isCompleted && (
                <IconButton onClick={() => setDialogOpen(true)}>
                  <CloseIcon
                    sx={{
                      color: '#E8192C',
                      fontSize: '1rem',
                    }}
                  />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </Row>

      {/* delete dialog */}
      <BaseDialog open={dialogOpen} onClose={handleClose} onConfirm={handleConfirm}>
        {t(translation.deleteKey)}
      </BaseDialog>
    </>
  );
};

type ManagerLiabilityManagersProps = {
  appData: AppData;
  disabledAdd?: boolean;
  dataList?: UploadSignedDocAgentInfo[];
  onReloadData?: () => void;
};

export const ManagerLiabilityManagers: FC<ManagerLiabilityManagersProps> = ({
  appData,
  disabledAdd,
  dataList = [],
  onReloadData,
}) => {
  const { appId, appStatus } = appData;
  const isCompleted = appStatus === AppStatusEnum.COMPLETED;

  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const lang = useLang();
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const [agentCode, setAgentCode] = useState<string>('');
  const [agentCodeError, setAgentCodeError] = useState('');
  const [agentInfo, setAgentInfo] = useState<UploadSignedDocAgentInfo | null>(null);

  const [showInput, setShowInput] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState('');

  const handleInputConfirm = async () => {
    const code = agentCode?.trim();
    if (!code || !/^\d{1,8}$/.test(code)) {
      setAgentCodeError(t('recruit.uploadSignedDocs.invalidAgentCode'));
      return;
    }
    setConfirmLoading(true);

    try {
      const value = preFillZero(code);
      const res = await getUplineAgentInfo(appId, { agentCode: value, lang: lang });
      if (!res || !res.result) {
        setAgentCodeError(t('recruit.uploadSignedDocs.invalidAgentCode'));
        return;
      }

      setAgentInfo(res.result);
      setAgentCodeError('');
      setShowDialog(true);
    } catch (err: any) {
      if (err?.statusCode === 400) {
        setAgentCodeError(t('recruit.uploadSignedDocs.invalidAgentCode'));
        return;
      } else {
        apiErrorHandler(err, dispatch);
      }
      console.log(err);
    } finally {
      setConfirmLoading(false);
    }
  };

  const resetForm = () => {
    setAgentInfo(null);
    setAgentCode('');
    setShowInput(false);
    setShowDialog(false);
  };
  const handleAdd = async () => {
    try {
      if (!agentInfo) {
        return;
      }
      await addUplineAgentInfo(appId, { agentCode: agentInfo.agentCode }, dispatch);
      await onReloadData?.();
      resetForm();
    } catch (err) {
      console.log(err);
    }
  };

  const handleDelete = async (agentCode: string) => {
    setDeleteLoading(agentCode);
    try {
      if (!agentCode) {
        return;
      }
      await deleteUplineAgentInfo(appId, { agentCode: agentCode }, dispatch);
      await onReloadData?.();
      resetForm();
    } catch (err) {
      console.log(err);
    } finally {
      setDeleteLoading('');
    }
  };

  const handleClose = () => {
    setShowDialog(false);
  };

  return (
    <div>
      <Row
        actions={[
          {
            type: RowActionEnum.ADD,
            disabled: disabledAdd || showInput || showDialog || isCompleted,
            onClick: () => setShowInput(true),
          },
        ]}
      >
        {t('recruit.uploadSignedDocs.signedManagers')}
      </Row>

      <div className={classes.managerTable}>
        <div className={classes.managerRow}>
          <div className={classes.managerInfoCells}>
            {MANAGER_TABLE_COLUMNS.map((col) => (
              <div key={col.key} className={classes.managerHeaderCell}>
                {col.labelKey ? t(col.labelKey) : ''}
              </div>
            ))}
          </div>
          <div className={classes.managerActionCell} />
        </div>

        {dataList?.map((row: UploadSignedDocAgentInfo, i) => (
          <div key={row.agentCode} className={classes.managerRow}>
            <div className={classes.managerInfoCells}>
              {[
                `${t('recruit.uploadSignedDocs.uplineManager')} ${i + 1}`,
                row.agentCode,
                row.agentName,
                row.title,
                row.status,
                row.email,
              ].map((value, idx) => (
                <div key={idx} className={classes.managerCell}>
                  {value}
                </div>
              ))}
            </div>

            {!isCompleted && (
              <IconButton
                className={classes.managerActionCell}
                disabled={!!deleteLoading}
                onClick={() => handleDelete(row.agentCode)}
              >
                {deleteLoading === row.agentCode ? (
                  <CircularProgress
                    size={16}
                    sx={{
                      color: '#E8192C',
                    }}
                  />
                ) : (
                  <CloseIcon
                    sx={{
                      color: '#E8192C',
                      fontSize: '1rem',
                    }}
                  />
                )}
              </IconButton>
            )}
            {isCompleted && <div className={classes.managerActionCell} />}
          </div>
        ))}

        {showInput && (
          <div className={classes.agentInfo}>
            <Tooltip title={`Upline Manager ${dataList.length + 1}`}>
              <div className={classes.managerCell} style={{ whiteSpace: 'nowrap' }}>
                {t('recruit.uploadSignedDocs.uplineManager')} {dataList.length + 1}
              </div>
            </Tooltip>
            <div className={classes.agentCodeInputContainer}>
              <div>
                <input
                  placeholder={t('recruit.uploadSignedDocs.agentCodePlaceholder')}
                  value={agentCode}
                  onChange={(e) => {
                    setAgentCode(e.target.value);

                    if (agentCodeError) {
                      setAgentCodeError('');
                    }
                  }}
                  className={classes.agentCodeInput}
                  style={{ border: `1px solid ${agentCodeError ? '#E1001F' : '#D1D5DB'}` }}
                />

                {agentCodeError && <div className={classes.agentCodeError}>{agentCodeError}</div>}
              </div>

              <div className={classes.confirmButtonContainer}>
                <Button variant="contained" color="secondary" disabled={confirmLoading} onClick={handleInputConfirm}>
                  {t('recruit.button.confirm')}
                  {confirmLoading && <CircularProgress style={{ marginLeft: 8 }} size={15} />}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* confirm dialog */}
      <BaseDialog open={showDialog} onClose={handleClose} onConfirm={handleAdd}>
        <div className={classes.reminderMessage}>
          {t('recruit.uploadSignedDocs.reminderMessage')}
          <br />
          <br />
          <span className={classes.confirmAddSpan}>{t('recruit.uploadSignedDocs.confirmAdd')}</span>
        </div>

        <div className={classes.managerDetailList}>
          {[
            {
              label: t('recruit.uploadSignedDocs.agentCode'),
              value: agentInfo?.agentCode,
            },
            {
              label: t('recruit.uploadSignedDocs.agentName'),
              value: agentInfo?.agentName,
            },
            {
              label: t('recruit.uploadSignedDocs.destination'),
              value: agentInfo?.title,
            },
            {
              label: t('recruit.uploadSignedDocs.agentStatus'),
              value: agentInfo?.status,
            },
            {
              label: t('recruit.uploadSignedDocs.emailAddress'),
              value: agentInfo?.email,
            },
          ].map(({ label, value }) => (
            <div key={label} className={classes.managerDetailItem}>
              <span>{label} :</span>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </BaseDialog>
    </div>
  );
};
